import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate, resolveImg } from "@/lib/api";
import { toast } from "sonner";
import DepthChart from "@/pages/DepthChart";
import ArticleThumb from "@/components/ArticleThumb";
import ArticleMeta from "@/components/ArticleMeta";
import { SocialProofBadge } from "@/components/SocialProof";
import { TitanTemperature } from "@/components/SignatureWidgets";

const FALLBACK = "https://images.pexels.com/photos/264305/pexels-photo-264305.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const SUBCATEGORIES = {
  "Breaking News": ["Injury Updates", "Transactions", "Coaches Comments", "AFC South Roundup", "Reports & Rumors"],
  "Analysis": ["Rankings", "Game Reactions", "Trending Topics", "Opponent Scouting Reports", "Film Room"],
  "Offseason": ["Roster Battles", "Player Developments", "Preseason"],
};

const CONFIG = {
  "breaking-news": {
    title: "Breaking News",
    label: "Live Wire",
    desc: "The biggest stories from Two-Tone Blue Nation — right as they hit.",
    category: "Breaking News",
    accent: "text-titans-red",
  },
  "analysis": {
    title: "Analysis",
    label: "Deep Dive",
    desc: "Film breakdowns, X's & O's, and beat-writer takes you won't find anywhere else.",
    category: "Analysis",
    accent: "text-titans-blue",
  },
  "offseason": {
    title: "Offseason",
    label: "Building The Roster",
    desc: "Free agency, draft, training camp — the long road back to Sunday.",
    category: "Offseason",
    accent: "text-titans-blue",
  },
};

export default function CategoryNews({ slug }) {
  const cfg = CONFIG[slug];
  const subs = useMemo(() => SUBCATEGORIES[cfg.category] || [], [cfg.category]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSub = searchParams.get("sub") || "All";

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const params = { category: cfg.category };
        if (activeSub !== "All") params.subcategory = activeSub;
        const { data } = await api.get("/news", { params });
        setArticles(data.articles || []);
      } catch {
        toast.error("Failed to load articles");
      } finally {
        setLoading(false);
      }
    })();
  }, [cfg.category, activeSub]);

  const pickSub = (s) => {
    if (s === "All") setSearchParams({});
    else setSearchParams({ sub: s });
  };

  return (
    <div data-testid={`${slug}-page`} className="max-w-[1400px] mx-auto px-5 md:px-8 py-16">
      <div className="mb-8">
        <div className={`font-cond uppercase tracking-[0.3em] text-xs ${cfg.accent}`}>{cfg.label}</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase tracking-wide section-title">{cfg.title}</h1>
        <p className="text-titans-silver mt-4 max-w-2xl font-body">{cfg.desc}</p>
      </div>

      {/* Subcategory filter chips */}
      <div className="mb-10 flex flex-wrap gap-2" data-testid={`${slug}-subcategories`}>
        {["All", ...subs].map((s) => (
          <button
            key={s}
            onClick={() => pickSub(s)}
            data-testid={`${slug}-sub-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`font-cond uppercase tracking-wider text-xs px-4 py-2 border transition-colors ${
              activeSub === s
                ? "bg-titans-blue border-titans-blue text-white"
                : "border-[rgba(138,141,143,0.3)] text-titans-silver hover:text-white hover:border-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Depth Chart subsection renders the interactive depth chart instead of articles */}
      {slug === "offseason" && activeSub === "Depth Chart" ? (
        <DepthChart />
      ) : loading ? (
        <div className="text-titans-silver">Loading…</div>
      ) : articles.length === 0 ? (
        <div className="titans-card p-10 text-center">
          <div className="font-display text-3xl uppercase tracking-wide">
            Nothing in {activeSub === "All" ? cfg.title : activeSub} yet.
          </div>
          <div className="text-titans-silver mt-2 font-body">
            Check back soon — new stories land here as they break.
          </div>
        </div>
      ) : (
        <>
          {/* Row 1 — Editorial hero + sidekicks + widget */}
          <section className="grid lg:grid-cols-12 gap-6 mb-14" data-testid={`${slug}-hero-row`}>
            {articles[0] && (
              <Link
                to={`/news/${articles[0].slug || articles[0].id}`}
                state={{ article: articles[0] }}
                data-testid={`${slug}-lead`}
                className="lg:col-span-7 titans-card group overflow-hidden flex flex-col hover-rise"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <ArticleThumb article={articles[0]} priority />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="bg-titans-red text-white font-cond uppercase tracking-wider text-[10px] px-2 py-1">
                        {articles[0].category || cfg.category}
                      </span>
                      {articles[0].subcategory && (
                        <span className="bg-titans-blue text-white font-cond uppercase tracking-wider text-[10px] px-2 py-1">
                          {articles[0].subcategory}
                        </span>
                      )}
                      <SocialProofBadge kind="trending" />
                    </div>
                    <h2 className="font-display uppercase text-white text-2xl md:text-4xl lg:text-5xl leading-[1.05] tracking-wide">
                      {articles[0].headline}
                    </h2>
                  </div>
                </div>
                <div className="p-5 md:p-6">
                  <ArticleMeta article={articles[0]} showBadge={false} className="mb-2" />
                  {articles[0].description && (
                    <p className="text-titans-silver font-body text-base md:text-lg leading-relaxed line-clamp-3">
                      {articles[0].description}
                    </p>
                  )}
                </div>
              </Link>
            )}
            <div className="lg:col-span-5 grid gap-4 content-start">
              {articles.slice(1, 3).map((a, i) => (
                <Link
                  key={a.id}
                  to={`/news/${a.slug || a.id}`}
                  state={{ article: a }}
                  data-testid={`${slug}-sidekick-${i}`}
                  className="titans-card group grid grid-cols-[130px_1fr] gap-0 overflow-hidden hover-rise"
                >
                  <div className="relative overflow-hidden">
                    <ArticleThumb article={a} />
                  </div>
                  <div className="p-4">
                    <div className="font-cond uppercase tracking-wider text-[10px] text-titans-blue mb-1">
                      {a.subcategory || a.category || cfg.category}
                    </div>
                    <h3 className="font-display uppercase text-white text-base leading-tight line-clamp-3 mb-2">
                      {a.headline}
                    </h3>
                    <ArticleMeta article={a} showBadge={false} size="xs" />
                  </div>
                </Link>
              ))}
              <div className="hidden lg:block">
                <TitanTemperature />
              </div>
            </div>
          </section>

          {/* Row 2 — Grid of the rest */}
          {articles.length > 3 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`${slug}-grid`}>
              {articles.slice(3).map((a, i) => (
                <Link
                  key={a.id}
                  to={`/news/${a.slug || a.id}`}
                  state={{ article: a }}
                  data-testid={`${slug}-item-${i}`}
                  className="titans-card group overflow-hidden flex flex-col hover-rise"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <ArticleThumb article={a} />
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 z-[2]">
                      <span className="bg-titans-red text-white font-cond uppercase tracking-wider text-[10px] px-2 py-1">
                        {a.category || cfg.category}
                      </span>
                      {a.subcategory && (
                        <span className="bg-titans-blue text-white font-cond uppercase tracking-wider text-[10px] px-2 py-1">
                          {a.subcategory}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-display uppercase text-white text-lg leading-tight line-clamp-3 mb-3">
                      {a.headline}
                    </h3>
                    {a.description && (
                      <p className="text-titans-silver text-sm line-clamp-2 font-body mb-3">
                        {a.description}
                      </p>
                    )}
                    <div className="mt-auto">
                      <ArticleMeta article={a} showBadge={false} size="xs" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
